import { createSlice } from "@reduxjs/toolkit";
import { resetStore } from "src/store/actions";
import { SalesTableData, SalesTableInitialState } from "../interface";
import { formatToCurrency, getComponentKey } from "src/utils/genericFunctions";
import stringToNumber from "src/utils/stringToNumber";
import { sumWeeklyData } from "../utils/formatData";
import {
  calculateTotalUnitsAndAverageCasesReceived,
  initialState
} from "./SalesTableReducer";
import { fetchSalesTableData } from "./SalesTableAsyncThunks";

const salesTableData = createSlice({
  name: "salesTable",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(resetStore, (state, action) => {
        return initialState;
      })
      .addCase(fetchSalesTableData.pending, (state) => {
        state.status = "pending";
        state.totalUnitsSold = "-";
      })
      .addCase(fetchSalesTableData.fulfilled, (state, { payload }) => {
        state.status = "idle";
        if (payload?.data?.length > 0) {
          const weeklyGroupedData = payload.data
            .map((item: any) => ({
              ...item,
              avgRetail: formatToCurrency(item.avgRetail),
              totalUnits: Number(item.totalUnits).toFixed(2),
              totalCases: Number(item.totalCases).toFixed(2),
              totalCasesSold: Number(item.totalCases).toFixed(2),
              totalCasesReceived: Number(item.totalCaseReceived).toFixed(2),
              totalCredits: formatToCurrency(stringToNumber(item.totalCredits)),
              extendedRetail: formatToCurrency(
                stringToNumber(item.extendedRetail)
              ),
              key: getComponentKey()
            }))
            // Group data by week
            .reduce((result: any, item: SalesTableData) => {
              const weekEnded = item?.weekEnded;

              if (
                stringToNumber(item.totalUnits) === 0 &&
                stringToNumber(item.totalCases) === 0 &&
                stringToNumber(item.totalCasesReceived) === 0
              ) {
                return result;
              }

              const subItem = {
                totalUnits: item.totalUnits,
                totalCases: item.totalCases,
                totalCasesSold: item.totalCasesSold,
                totalCasesReceived: item.totalCasesReceived,
                totalCredits: item.totalCredits,
                avgRetail: item.avgRetail,
                extendedRetail: item.extendedRetail
              };

              if (!result[weekEnded]) {
                result[weekEnded] = { ...item, subItems: [subItem] };

                return result;
              }

              result[weekEnded] = sumWeeklyData(result[weekEnded], item);

              result[weekEnded].avgRetail = formatToCurrency(
                stringToNumber(result[weekEnded].extendedRetail) /
                +result[weekEnded].totalUnits
              );
              result[weekEnded].totalUnits =
                result[weekEnded].totalUnits.toFixed(2);
              result[weekEnded].subItems.push(subItem);
              result[weekEnded].useCollapsibleRow = true;

              return result;
            }, {});

          const data = Object.values(weeklyGroupedData) as SalesTableData[];
          const { salesDataWithAvgCasesReceived, totalUnitsSold } =
            calculateTotalUnitsAndAverageCasesReceived(data);
          state.data = salesDataWithAvgCasesReceived;
          state.totalUnitsSold = totalUnitsSold;
          state.recordsCount = payload.recordsCount;
        } else {
          state.totalUnitsSold = "0";
          state.data = [];
        }
      })
      .addCase(fetchSalesTableData.rejected, (state) => {
        state.status = "error";
        state.data = [];
        state.totalUnitsSold = "-";
      });
  }
});

export const selectSalesTableData = ({
  salesTable
}: {
  salesTable: SalesTableInitialState;
}) => salesTable.data;

export const selectSalesTableRecordsCount = ({
  salesTable
}: {
  salesTable: SalesTableInitialState;
}) => salesTable.recordsCount;

export const selectSalesTableApiStatus = ({
  salesTable
}: {
  salesTable: SalesTableInitialState;
}) => salesTable.status;

export const selectSalesTableError = ({
  salesTable
}: {
  salesTable: SalesTableInitialState;
}) => salesTable.error;

export default salesTableData.reducer;
